import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetOverlay,
} from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@frontend/shadcn/components/ui/tabs';
import { Alert, AlertDescription } from '@frontend/shadcn/components/ui/alert';
import { TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import {
  XmarkIcon,
  ArrowLeftIcon,
  CircleInfoIcon,
  AtIcon,
  LocationDotIcon,
  UserGroupIcon,
} from '@revfluence/fresh-icons/solid/esm';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import { CreatorDiscoveryEmailResource } from '@frontend/app/hooks';

import { UserIcon } from '@revfluence/fresh-icons/regular/esm';

import { QueryVariables } from '../types';
import { useCreatorPostsGridProps } from './components/creator-posts-grid/hooks';
import { CreatorPostsGrid } from './components/creator-posts-grid';
import { useInstagramDemographicsProps } from './hooks/useInstagramDemographics';
import { DemographicsSection } from '@/shadcn/components/demographics';
import { useSocialStatsProps } from './components/social-stats-section/hooks';
import { SocialStatsSection } from '@/shadcn/components/social-stats';
import { SimilarCreatorsSection } from './components/similar-creators-section';
import { useCreatorOverviewActions } from './hooks/use-creator-overview-actions';
import {
  SheetGrid,
  SheetTopBar,
  SheetColumn,
  PreviewSheetContent,
  SheetHeaderWithClose,
  SheetTopBarPlatformInfo,
} from '@/shadcn/components/sheet-extensions';
import { SheetTopBarActionMenu } from '@/shadcn/components/sheet-extensions';
import { CreatorOverviewActions } from '@/applications/Social/components/content-preview/components/sheet-top-bar';
import { getCountryLabel } from '../components/filters-form/utils';
import { ProfileSection, ProfileAvatar, ProfileHandle, ProfileMetadataRow, ProfileNameRow } from '@/shadcn/components/profile';

interface InstagramCreatorOverviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creator: InstagramCreator | null;
  isLoading: boolean;
  isLoadingDetailedData?: boolean;
  isFetchingSimilarCreators: boolean;
  similarCreators: InstagramCreator[] | null;
  originalCreator: Pick<InstagramCreator, 'username'> | null;
  onReturnToOriginalCreator: () => void;
  showReturnButton: boolean;
  updateUrlParams?: (params: Partial<QueryVariables>) => void;
  handleSearch?: () => void;
  queryVariables?: QueryVariables;
  onInviteCreator?: () => void | Promise<void>;
  isInviting?: boolean;
  isCreatorInvited?: boolean;
  emailResource?: CreatorDiscoveryEmailResource;
  igdmActive?: boolean;
  /** Optional trigger element rendered outside the Sheet. When provided, clicking it opens the sheet. */
  children?: React.ReactNode;
}

export const InstagramCreatorOverview: React.FC<InstagramCreatorOverviewProps> = ({
  open,
  onOpenChange,
  creator,
  isLoading,
  isLoadingDetailedData,
  isFetchingSimilarCreators,
  similarCreators,
  originalCreator,
  onReturnToOriginalCreator,
  showReturnButton,
  updateUrlParams,
  handleSearch,
  queryVariables,
  onInviteCreator,
  isInviting = false,
  isCreatorInvited = false,
  emailResource,
  igdmActive = false,
  children,
}) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [showVerificationAlert, setShowVerificationAlert] = useState(true);

  // Initialize action menu hook
  const actionMenuProps = useCreatorOverviewActions({
    creator,
    emailResource,
    igdmActive,
  });

  // Create the getInviteDisabledReason function for the composable component
  const getInviteDisabledReason = () => {
    if (!actionMenuProps.canBeInvited) {
      return igdmActive
        ? 'Creator has no email address and is not in Instagram Creator Marketplace'
        : 'Creator has no email address';
    }
    return null;
  };

  useEffect(() => {
    if (!open) {
      setActiveTab('summary');
      setShowVerificationAlert(true);
    }
  }, [open]);

  const creatorPostsGridProps = useCreatorPostsGridProps(creator, isLoadingDetailedData ?? isLoading);
  const majorAudienceDemographicsProps = useInstagramDemographicsProps(creator, isLoadingDetailedData ?? isLoading, 'age', 'stacked');
  const socialStatsProps = useSocialStatsProps(isLoadingDetailedData ?? isLoading, creator);

  const handleSimilarCreatorClick = (clickedCreator: InstagramCreator) => {
    // Close the drawer
    onOpenChange(false);

    // Update URL params to set similarToCreators filter and remove creatorUsername
    if (updateUrlParams && queryVariables) {
      updateUrlParams({
        ...queryVariables,
        similarToCreators: [clickedCreator.username],
        usernames: [clickedCreator.username], // Also set usernames with the same creator
        creatorUsername: '', // Clear the creator username to close drawer
      });
    }

    // Trigger search with new parameters
    if (handleSearch) {
      handleSearch();
    }
  };

  const handleDismissVerificationAlert = () => {
    setShowVerificationAlert(false);
  };

  // Only return null when the sheet should close (not open)
  if (!open) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}
      <SheetOverlay className="z-[190]" />
      <SheetContent
        className="w-full overflow-y-auto p-0 z-[200] sm:max-w-[1000px] [&>button:first-of-type]:hidden flex flex-col"
        side="right"
      >
        <TooltipProvider>
          <div className="flex flex-col h-full flex-1">
            <SheetHeaderWithClose
              title="Creator Overview"
              onClose={() => onOpenChange(false)}
              closeAriaLabel="Close overview"
            />

            {/* NEW REFACTORED SheetTopBar using composable components */}
            <SheetTopBar>
              <div className="flex items-center gap-2">
                {showReturnButton && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onReturnToOriginalCreator}
                    className="p-2 border border-grey-6 rounded-lg"
                    aria-label={`Return to @${originalCreator.username}'s profile`}
                    data-dd-action-name="instagram-creator-overview-return-to-original"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                  </Button>
                )}
                {creator ? (
                  <SheetTopBarPlatformInfo
                    platform="instagram"
                    clickable
                    onClick={() => window.open(`https://www.instagram.com/${creator.username}`, '_blank', 'noopener,noreferrer')}
                    username={creator.username}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-6 rounded" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                )}
              </div>

              {/* Composable approach */}
              <SheetTopBarActionMenu
                disabled={isLoading}
              >
                <CreatorOverviewActions
                  creator={creator}
                  igdmActive={igdmActive}
                  disabled={isLoading}
                  filteredCampaigns={actionMenuProps.filteredCampaigns}
                  filteredGroups={actionMenuProps.filteredGroups}
                  campaignSearchTerm={actionMenuProps.campaignSearchTerm}
                  setCampaignSearchTerm={actionMenuProps.setCampaignSearchTerm}
                  groupSearchTerm={actionMenuProps.groupSearchTerm}
                  setGroupSearchTerm={actionMenuProps.setGroupSearchTerm}
                  onInviteToCampaign={actionMenuProps.onInviteToCampaign}
                  onAddToGroup={actionMenuProps.onAddToGroup}
                  onAddToCampaign={actionMenuProps.onAddToCampaign}
                  canBeInvited={actionMenuProps.canBeInvited}
                  getInviteDisabledReason={getInviteDisabledReason}
                />
              </SheetTopBarActionMenu>
            </SheetTopBar>

            <SheetGrid>
              {/* Left column with creator profile and recent posts */}
              <SheetColumn className="md:col-span-1 flex flex-col h-full">
                <PreviewSheetContent className="flex-1">
                  {/* Profile Section */}
                  <ProfileSection>
                    {(isLoading || isLoadingDetailedData || !creator) ? (
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-16 w-16 rounded-full" />
                        <div className="flex flex-col gap-1 flex-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                          <Skeleton className="h-3 w-36" />
                          <Skeleton className="h-3 w-40" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <ProfileAvatar
                          src={creator.profilePictureUrl ?? undefined}
                          alt={creator.name ?? creator.username}
                          name={creator.name ?? creator.username}
                          size="h-16 w-16"
                        />
                        <div className="flex flex-col">
                          <ProfileHandle
                            handle={creator.username}
                            platform="instagram"
                            onProfileClick={() => window.open(`https://www.instagram.com/${creator.username}`, '_blank', 'noopener,noreferrer')}
                          />
                          <ProfileNameRow
                            icon={<UserIcon className="h-3 w-3 text-grey-5" />}
                            name={creator.name ?? ''}
                            fallback="DATA NEEDED"
                          />
                          <ProfileMetadataRow
                            icon={<AtIcon className="h-3 w-3 text-grey-5" />}
                            truncate
                            tooltipContent={creator.email}
                          >
                            {creator.email ?? '--'}
                          </ProfileMetadataRow>
                          <ProfileMetadataRow icon={<UserGroupIcon className="h-3 w-3 text-grey-5" />}>
                            {creator.insights?.total?.followers !== undefined && creator.insights?.total?.followers !== null ?
                              creator.insights.total.followers.toLocaleString() : 'DATA NEEDED'}
                            <span className="flex items-center gap-1 ml-2">
                              <LocationDotIcon className="h-3 w-3 text-grey-5" />
                              {creator.country ? getCountryLabel(creator.country) : 'DATA NEEDED'}
                            </span>
                          </ProfileMetadataRow>
                        </div>
                      </div>
                    )}
                  </ProfileSection>
                  <CreatorPostsGrid
                    posts={creatorPostsGridProps.recentPosts}
                    isLoading={creatorPostsGridProps.isLoading}
                    creatorUsername={creator?.username ?? ''}
                  />
                </PreviewSheetContent>
              </SheetColumn>

              {/* Right column – existing content */}
              <SheetColumn className="md:col-span-2">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <div className="border-b mt-3">
                    <TabsList className="border-0 bg-transparent m-0">
                      <TabsTrigger
                        value="summary"
                        className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                        data-dd-action-name="instagram-creator-overview-summary-tab"
                      >
                        Summary
                      </TabsTrigger>
                      <TabsTrigger
                        value="similar"
                        className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                        data-dd-action-name="instagram-creator-overview-similar-creators-tab"
                      >
                        Similar Creators
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  {creator && !creator.inCreatorMarketplace && showVerificationAlert && !isLoading && (
                    <Alert className="flex items-center justify-center gap-2 h-[58px] w-full px-4 py-2 rounded-md border border-amber-300 bg-amber-50">
                      <AlertDescription className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <CircleInfoIcon className="w-4 h-4 flex-shrink-0 text-amber-500" />
                          <span className="text-sm text-gray-900">
                            This creator isn't onboarded to the Instagram Creator Marketplace.
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isCreatorInvited ? (
                            <span className="text-sm text-green-600 font-medium">
                              Invited
                            </span>
                          ) : (
                            <Button
                              variant="link"
                              size="sm"
                              className="text-blue-600 p-0 h-auto font-medium"
                              onClick={onInviteCreator}
                              disabled={isInviting}
                              data-dd-action-name="instagram-creator-overview-invite-creator"
                            >
                              {isInviting ? 'Inviting...' : 'Invite'}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDismissVerificationAlert}
                            className="p-1 h-auto text-gray-400 hover:text-gray-600"
                            aria-label="Dismiss alert"
                            data-dd-action-name="instagram-creator-overview-dismiss-verification-alert"
                          >
                            <XmarkIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  <TabsContent value="summary" className="mt-4">
                    <div className="space-y-8">
                      <div>
                        <SocialStatsSection {...socialStatsProps} />
                      </div>
                      <div>
                        <DemographicsSection {...majorAudienceDemographicsProps} />
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="similar" className="mt-4">
                    <SimilarCreatorsSection
                      similarCreators={similarCreators}
                      isFetching={isFetchingSimilarCreators}
                      onCreatorClick={handleSimilarCreatorClick}
                    />
                  </TabsContent>
                </Tabs>
              </SheetColumn>
            </SheetGrid>
          </div>
        </TooltipProvider>
      </SheetContent>
    </Sheet>
  );
};

export default InstagramCreatorOverview;
